import styled from "@emotion/styled";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Spinner,
} from "@fluentui/react-components";
import { ArrowImportRegular, Dismiss24Filled } from "@fluentui/react-icons";
import { Buffer } from "buffer";
import { toast } from "components/FluentToast";
import { VSpace } from "components/Spacer";
import { Flex } from "components/styled";
import { DefaultButton, Icon } from "office-ui-fabric-react";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { useImportedInvoicesStore } from "store/ImportedInvoicesStore";
import { ImportedInvoiceImportResult } from "types/importedInvoices";
import { ImportResultReport } from "./ImportResultReport";

const FormSection = styled.section`
  display: flex;
  flex-wrap: wrap;
  background-color: rgba(201, 201, 201, 0.1);
`;

const FormSectionItem100 = styled.div`
  width: 100%;
  margin: 9px;
`;

const FileName = styled.div`
  font-size: 20px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Title = styled.div`
  font-size: 20px;
`;

const UploadIcon = styled(Icon)`
  font-size: 35px;
`;

// Uploads the QTG invoice export (xlsx). Invoices already in PCT are replaced by the file's
// data, new ones are added; the file's own period plays no role. The import report stays
// visible until the dialog is closed.
export const ImportImportedInvoicesDialog = () => {
  const [
    { importImportedInvoicesDialog, importImportedInvoicesStatus, lastImportResult },
    {
      setImportedInvoicesStoreValues,
      importImportedInvoices,
      applyImportedInvoices,
      loadImportedInvoices,
    },
  ] = useImportedInvoicesStore();
  const isLoading = importImportedInvoicesStatus === "loading";
  const { t } = useTranslation();
  const [file, setFile] = useState<{ base64str: string; name: string } | null>(null);
  const result: ImportedInvoiceImportResult | null = lastImportResult;

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => {
        const encode = Buffer.from(reader.result as any).toString("base64");
        setFile({ base64str: encode, name: f.name });
      };
      reader.readAsArrayBuffer(f);
    });
  }, []);

  const { getRootProps, getInputProps, isDragAccept, isDragReject, open } =
    useDropzone({
      noDragEventsBubbling: true,
      noClick: true,
      noKeyboard: true,
      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      },
      multiple: false,
      onDrop,
    });

  const close = () => {
    setImportedInvoicesStoreValues({
      importImportedInvoicesDialog: { isOpen: false },
      lastImportResult: null,
    });
  };

  return (
    <Dialog open={importImportedInvoicesDialog.isOpen}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t("pct.importImportedInvoices.label")}</DialogTitle>
          <DialogContent>
            <VSpace height={10} />
            <MessageBar intent="info" data-help="imported-invoices-import-info">
              <MessageBarBody>{t("pct.importImportedInvoicesInfo.label")}</MessageBarBody>
            </MessageBar>
            <VSpace height={15} />
            {result ? (
              <div data-help="imported-invoices-import-result">
                <ImportResultReport result={result} />
              </div>
            ) : (
              <>
                <FormSection>
                  <FormSectionItem100>
                    <div
                      data-help="imported-invoices-import-file"
                      className={isDragAccept ? "accept" : isDragReject ? "reject" : ""}
                      {...getRootProps()}
                    >
                      <input {...(getInputProps() as any)} />
                      {file ? (
                        <Flex direction="column" gap={10}>
                          <Flex gap={5} justifyContent="center">
                            <Icon style={{ fontSize: "25px" }} iconName="ExcelDocument" />
                            <FileName title={file.name}>{file.name}</FileName>
                            <Button
                              appearance="subtle"
                              icon={<Dismiss24Filled />}
                              onClick={() => {
                                setFile(null);
                              }}
                              disabled={isLoading}
                            />
                          </Flex>
                          <DefaultButton text={t("pct.chooseDifferentFile")} onClick={open} disabled={isLoading} />
                        </Flex>
                      ) : (
                        <Flex direction="column" gap={5}>
                          <UploadIcon iconName="CloudUpload" />
                          <div style={{ textAlign: "center" }}>
                            <Title>{t("pct.dragExcelFileHere")}</Title>
                            <Title>{t("pct.or")}</Title>
                          </div>
                          <DefaultButton text={t("pct.browseFiles")} onClick={open} />
                        </Flex>
                      )}
                    </div>
                  </FormSectionItem100>
                </FormSection>
              </>
            )}
            <VSpace height={20} />
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={isLoading} onClick={close}>
              {t("pct.close.label")}
            </Button>
            {!result ? (
              <Button
                data-help="imported-invoices-import-submit"
                appearance="primary"
                disabled={isLoading || !file}
                icon={isLoading ? <Spinner size="tiny" /> : <ArrowImportRegular fontSize={16} />}
                onClick={() => {
                  if (!file) return;
                  importImportedInvoices(
                    {
                      name: file.name,
                      base64String: file.base64str,
                    },
                    (importResult: ImportedInvoiceImportResult) => {
                      toast.success(t("greco.success"));
                      applyImportedInvoices({ importedInvoices: importResult.importedInvoices, t });
                      // the new rows outside the loaded range come with a reload
                      loadImportedInvoices({ t });
                    }
                  );
                }}
              >
                {t("pct.import.label")}
              </Button>
            ) : null}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
